/**
 * Icon catalog for projects AND reports (PR-1 builder reorg).
 *
 * Icons persist as plain strings (the PascalCase lucide component
 * name) on ``projects.icon`` and on each report's
 * ``report_config.icon``. The portal resolves them via
 * ``hp-portal/frontend/src/lib/icons.ts`` (``iconFromName``), which
 * falls back to a default icon for names it doesn't know — so the
 * builder's catalog can grow ahead of the portal without breaking it.
 * Fase 2 syncs the portal map to this list.
 *
 * The first 9 entries are the portal's current canonical set; the rest
 * is the PR-1 expansion ("muchos más iconos" — decisión D9).
 */
import {
  Activity,
  BarChart3,
  Banknote,
  Bell,
  Bot,
  Boxes,
  Brain,
  Building2,
  Calculator,
  Compass,
  CreditCard,
  Database,
  Factory,
  FileText,
  Flag,
  Flame,
  FlaskConical,
  Folder,
  Gauge,
  Globe,
  Handshake,
  Layers,
  Leaf,
  LineChart,
  Map,
  MapPin,
  Microscope,
  Monitor,
  Package,
  Palette,
  PieChart,
  Plane,
  Printer,
  Receipt,
  Recycle,
  Rocket,
  Search,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Snowflake,
  Sparkles,
  Star,
  Store,
  Tag,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Warehouse,
  Zap,
  type LucideIcon,
} from "lucide-react";

export const PROJECT_ICONS: ReadonlyArray<{
  name: string;
  component: LucideIcon;
}> = [
  // Canonical portal set (hp-portal/frontend/src/lib/icons.ts)
  { name: "BarChart3", component: BarChart3 },
  { name: "ShieldCheck", component: ShieldCheck },
  { name: "Database", component: Database },
  { name: "LineChart", component: LineChart },
  { name: "PieChart", component: PieChart },
  { name: "Activity", component: Activity },
  { name: "Globe", component: Globe },
  { name: "Layers", component: Layers },
  { name: "Sparkles", component: Sparkles },
  // PR-1 expansion
  { name: "TrendingUp", component: TrendingUp },
  { name: "TrendingDown", component: TrendingDown },
  { name: "ShoppingCart", component: ShoppingCart },
  { name: "Store", component: Store },
  { name: "Package", component: Package },
  { name: "Boxes", component: Boxes },
  { name: "Truck", component: Truck },
  { name: "Plane", component: Plane },
  { name: "Factory", component: Factory },
  { name: "Warehouse", component: Warehouse },
  { name: "Building2", component: Building2 },
  { name: "Wallet", component: Wallet },
  { name: "CreditCard", component: CreditCard },
  { name: "Banknote", component: Banknote },
  { name: "Receipt", component: Receipt },
  { name: "Tag", component: Tag },
  { name: "Target", component: Target },
  { name: "Gauge", component: Gauge },
  { name: "Rocket", component: Rocket },
  { name: "Star", component: Star },
  { name: "Zap", component: Zap },
  { name: "Flame", component: Flame },
  { name: "Leaf", component: Leaf },
  { name: "Recycle", component: Recycle },
  { name: "Snowflake", component: Snowflake },
  { name: "FlaskConical", component: FlaskConical },
  { name: "Microscope", component: Microscope },
  { name: "Search", component: Search },
  { name: "Calculator", component: Calculator },
  { name: "Monitor", component: Monitor },
  { name: "Smartphone", component: Smartphone },
  { name: "Printer", component: Printer },
  { name: "Brain", component: Brain },
  { name: "Bot", component: Bot },
  { name: "Users", component: Users },
  { name: "Handshake", component: Handshake },
  { name: "Map", component: Map },
  { name: "MapPin", component: MapPin },
  { name: "Compass", component: Compass },
  { name: "Folder", component: Folder },
  { name: "FileText", component: FileText },
  { name: "Bell", component: Bell },
  { name: "Flag", component: Flag },
  { name: "Palette", component: Palette },
];

export function findIconComponent(
  name: string | null | undefined,
): LucideIcon | null {
  if (!name) return null;
  return PROJECT_ICONS.find((entry) => entry.name === name)?.component ?? null;
}
